import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { PlatformConfiguration } from 'src/common/types/ConfigurationTypes';

import { initCoralogixRum } from '../../services/analytics/CoralogixRumService';
import { isLoggedIn } from '../../services/AuthenticationService';
import { getCentralConfig } from '../../services/central-configuration/CentralConfigurationService';
import { DevelopmentToggles, getSingleToggle } from '../../services/TogglesService';
import { useUserTenant } from '../userTenantProvider/UserTenantProvider';

type TChatbotLinkConfig = { link: string; name: string };

export type TConfiguration = Record<string, Record<string, unknown>> & {
	GLOBAL?: {
		CONFIG?: {
			SLACK_CHATBOT?: TChatbotLinkConfig;
			MS_TEAMS_CHATBOT?: TChatbotLinkConfig;
		};
		TOGGLES?: {
			ENABLE_CHATBOT_LINK?: boolean;
		};
	};
	PLATFORM?: PlatformConfiguration;
};
export type TGetToggle = (
	toggleId: DevelopmentToggles,
	// useUserToggle: bc for auth0 use toggle support. Deafult is false
	// If true, will try to extract auth0 value toggle from the user's token
	// If false, will try to extract the value from the central configuration instead
	useUserToggle?: boolean
) => boolean;

interface IConfigurationContext {
	configuration: TConfiguration;
	getToggle: TGetToggle;
}

const ConfigurationContext = createContext<IConfigurationContext>(null);

interface ConfigurationProviderProps {
	children: ReactNode;
}

const ConfigurationProvider: FC<ConfigurationProviderProps> = ({ children }) => {
	const [configuration, setConfiguration] = useState<TConfiguration>(null);
	const { tenant } = useUserTenant();
	const hasInitializedCoralogix = useRef(false);

	const normalizeBooleanToggleValue = (toggle: string | boolean): string | boolean => {
		if (toggle === 'true') {
			return true;
		}
		if (toggle === 'false') {
			return false;
		}
		return toggle;
	};

	const getToggle = useCallback(
		(toggleId: DevelopmentToggles, useUserToggle = false): boolean => {
			const localToggleValue = getSingleToggle(toggleId, useUserToggle);
			return useUserToggle
				? localToggleValue
				: localToggleValue ||
						!!normalizeBooleanToggleValue(configuration?.TOGGLES?.[toggleId] as string | boolean);
		},
		[configuration]
	);

	const initConfiguration = useCallback(async (): Promise<void> => {
		if (!configuration) {
			const formattedConfig: TConfiguration = await getCentralConfig(tenant.products);
			setConfiguration(formattedConfig);

			// Initialize Coralogix RUM after configuration is loaded
			if (!hasInitializedCoralogix.current) {
				hasInitializedCoralogix.current = true;
				initCoralogixRum();
			}
		}
	}, [tenant, configuration]);

	useEffect(() => {
		if (tenant && isLoggedIn()) {
			initConfiguration();
		}
	}, [initConfiguration, tenant]);

	const value = useMemo(() => ({ configuration, getToggle }), [configuration, getToggle]);

	return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
};

const useConfiguration = (): IConfigurationContext => useContext(ConfigurationContext);

export { ConfigurationProvider, useConfiguration };
